import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';

import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatTableModule } from '@angular/material/table';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { FormControl as NgFormControl } from '@angular/forms';
import { Observable, map, startWith } from 'rxjs';
import { Effort } from '../../models/effort';
import { SlugInputDirective } from '../../directives/slug-input.directive';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { deptChooserComponent } from '../../components/dialogs/dept-chooser/dept-chooser.component';
import { Group } from '../../models/group';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-effort-editor',
  standalone: true,
  templateUrl: './effort-editor.component.html',
  styleUrls: ['./effort-editor.component.scss'],
  imports: [
    FormsModule,
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTableModule,
    MatIconModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatSelectModule,
    SlugInputDirective,
    MatCheckboxModule,
    DragDropModule
  ],
})
export class EffortEditorComponent {
  title: string = "Edit Effort";
  form: FormGroup;

  tagControl = new NgFormControl('');
  filteredTags$: Observable<string[]>;

  allTags: string[] = ['Admin', 'Finance', 'HR', 'IT', 'Root']; // Example tag list

  displayedColumns: string[] = ['displayName', 'email', 'associationType'];
  deptName = '';

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<EffortEditorComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { isNew: boolean, effort: Effort | null }
  ) {
    this.title = this.data?.isNew ? "Add New Effort" : "Edit Effort";
    const effort = structuredClone(this.data.effort) as Effort | undefined;
    this.deptName = effort?.principalGroup?.name ?? '';

    // build reactive form; make 'key' required
    this.form = this.fb.group({
      id: [effort?.id],
      title: [effort?.title ?? ''],
      key: [effort?.key ?? '', [Validators.required]],
      tags: [effort?.tags ?? []],
      users: [effort?.users ?? []],
      principalGroupId: [effort?.principalGroupId ?? null],
      isDeleted: [effort?.isDeleted ?? false]
    });

    this.filteredTags$ = this.tagControl.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterTags(value || ''))
    );
  }

  private _filterTags(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.allTags.filter((tag) =>
      tag.toLowerCase().includes(filterValue)
    );
  }

  addTag(tag: string) {
    if (!tag) return;
    const tags = [...(this.form.get('tags')!.value || [])];
    if (!tags.includes(tag)) {
      tags.push(tag);
      this.form.get('tags')!.setValue(tags);
    }
    this.tagControl.setValue('');
  }

  removeTag(tag: string) {
    const tags = (this.form.get('tags')!.value || []).filter((t: string) => t !== tag);
    this.form.get('tags')!.setValue(tags);
  }

  save() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const effortOut: Effort = { ...(this.form.value as Effort) };
    this.dialogRef.close({ effort: effortOut, isNew: this.data.isNew });
  }

  selectDept(dept: Group) {
    this.form.patchValue({ principalGroupId: dept.id });
    this.deptName = dept.name;
  }

  cancel() {
    this.dialogRef.close();
  }

  openDeptSelectDialog(): void {
    const dialogRef = this.dialog.open(deptChooserComponent, {
      width: '500px',
      maxHeight: '50vh'
    });

    dialogRef.afterClosed().subscribe((selectedDept: Group) => {
      if (selectedDept) {
        this.selectDept(selectedDept);
      }
    });
  }
}
